import Axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  ImportOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  GuideContent,
  PageContainer,
  PageHeader,
  SectionCard,
} from '../../common';

const { TextArea } = Input;

const emptyContent = {
  eyebrow: 'はじめにお読みください',
  title: '',
  meta: [],
  sections: [{ title: '概要', body_md: '' }],
  points: [],
  note: '',
};

const emptyGuide = {
  guide_key: '',
  title: '',
  survey_code: '',
  type_360: 'all',
  relation_scope: 'all',
  sub_type: 'all',
  variant_key: 'default',
  priority: 10,
  active: true,
  content: emptyContent,
};

const templateVariables = [
  { token: '{$survey_start_date$}', description: '回答開始日' },
  { token: '{$survey_end_date$}', description: '回答終了日' },
  { token: '{$hero_name$}', description: '対象者名' },
  { token: '{$target_name$}', description: '評価対象者名' },
  { token: '{$relation_label$}', description: '回答者区分（本人・上司・同僚・部下）' },
  { token: '{$target_label$}', description: '評価対象者名の向け表示（例: 田中 太郎 様向け）' },
  { token: '{$t_name$}', description: '研修名・トレーニング名' },
  { token: '{$user_company$}', description: '回答者の会社名' },
  { token: '{$survey_name$}', description: 'サーベイ名' },
  { token: '{$question_count$}', description: '設問数' },
];

const templateVariableHelp = (
  <div style={{ maxWidth: 440 }}>
    <div style={{ marginBottom: 8 }}>
      表示内容の各テキストで使用できます。回答画面では回答者の情報に合わせて自動置換されます。
    </div>
    {templateVariables.map((item) => (
      <div
        key={item.token}
        style={{
          display: 'grid',
          gridTemplateColumns: '170px 1fr',
          gap: 8,
          marginTop: 4,
        }}
      >
        <code>{item.token}</code>
        <span>{item.description}</span>
      </div>
    ))}
    <div style={{ marginTop: 8 }}>
      未定義の変数は置換されず、そのまま表示されます。
    </div>
  </div>
);

const jsonImportHelp = (
  <div style={{ maxWidth: 420 }}>
    <div style={{ marginBottom: 8 }}>
      JSONで本文を取り込む場合、文字列内の改行は「\n」で記述することを推奨します。
    </div>
    <div style={{ marginBottom: 8 }}>
      フォーム上でEnter入力した改行は、そのまま改行として保存されます。
    </div>
    <div>
      「\\n」や文字列内の実際の改行が含まれている場合も、取込時に改行として扱います。
    </div>
    <div style={{ marginTop: 8 }}>
      ファイル選択はブラウザ内で読み込むだけで、サーバーへファイル送信は行いません。
    </div>
  </div>
);

const noteHelp = (
  <div style={{ maxWidth: 460 }}>
    <div style={{ marginBottom: 8 }}>
      ガイド本文の最後に表示する補足カードです。注意事項、問い合わせ先、匿名性の説明など、本文とは分けて強調したい短い案内に使用します。
    </div>
    <div style={{ marginBottom: 4 }}>
      この欄はMarkdown形式で表示されます。入力後は右側のプレビューで確認してください。
    </div>
    <div>
      行頭の <code>- </code> は箇条書き、<code>**文字**</code> は太字になります。
    </div>
  </div>
);

const markdownHelp = (
  <div style={{ maxWidth: 460 }}>
    <div style={{ marginBottom: 8 }}>
      この本文欄はMarkdown形式で表示されます。入力後は右側のプレビューで確認してください。
    </div>
    <div style={{ marginBottom: 4 }}>
      Enterで入力した改行は、表示画面でも改行として扱われます。
    </div>
    <div style={{ marginBottom: 4 }}>
      行頭の <code>- </code> は箇条書き、<code>1. </code> は番号付きリストになります。
    </div>
    <div style={{ marginBottom: 4 }}>
      <code>**文字**</code> は太字、行頭の <code># </code> は見出しになります。
    </div>
    <div>
      記号をそのまま表示したい場合は、右側のプレビューで見え方を確認してください。
    </div>
  </div>
);

const basicInfoHelps = {
  guideKey:
    '回答案内を一意に識別する管理用キーです。回答者画面には表示されません。例: 60020_bsfs_self',
  title:
    '一覧・検索で使う管理用タイトルです。回答者画面には表示されません。表示されるタイトルは「ガイド本文タイトル」です。',
  surveyCode:
    'どのサーベイコードに紐づけるかを指定します。回答者のサーベイコードと一致した案内が候補になります。',
  priority:
    '同じ条件に複数の案内が一致した場合に使う優先度です。数値が大きい案内が優先されます。通常は10のままで問題ありません。',
  type360:
    '診断の細かい種類を分けるための条件です。例: bsfs はアドバンス、bsfs_basic はベーシック。分けない場合は all を入力します。',
  relationScope:
    '本人用・協力者用を分けるための条件です。all は共通、self は本人用、other は協力者用です。',
  subType:
    'さらに細かい分類が必要な場合だけ使う条件です。通常は all を入力します。',
  variantKey:
    '同じ診断内で事前・直後・事後などを分けるための条件です。通常は default を入力します。',
};

const requiredLabel = (label) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <span
      aria-hidden="true"
      style={{
        color: 'var(--ant-color-error, #d44a18)',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      *
    </span>
    {label}
  </span>
);

const helpLabel = (label, help) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {label}
    <Tooltip title={help} overlayStyle={{ maxWidth: 420 }}>
      <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
    </Tooltip>
  </span>
);

const variableHelpLabel = (label) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {label}
    <Tooltip title={templateVariableHelp} overlayStyle={{ maxWidth: 520 }}>
      <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
    </Tooltip>
  </span>
);

const jsonImportTitle = (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    JSON取込
    <Tooltip title={jsonImportHelp} overlayStyle={{ maxWidth: 500 }}>
      <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
    </Tooltip>
  </span>
);

const noteHelpLabel = (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    補足メモ（Markdown）
    <Tooltip title={noteHelp} overlayStyle={{ maxWidth: 520 }}>
      <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
    </Tooltip>
  </span>
);

const markdownHelpLabel = (label) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    {label}
    <Tooltip title={markdownHelp} overlayStyle={{ maxWidth: 520 }}>
      <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
    </Tooltip>
  </span>
);

function cloneGuide(guide) {
  return JSON.parse(JSON.stringify(guide || emptyGuide));
}

function safeFileName(value) {
  return String(value || 'survey-guide')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_');
}

function normalizeText(value, fallback = '') {
  if (value === undefined || value === null) return fallback;
  return String(value)
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function escapeRawLineBreaksInJsonStrings(text) {
  let result = '';
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (!inString) {
      result += char;
      if (char === '"') inString = true;
      continue;
    }

    if (escaped) {
      if (char === '\r') {
        if (text[index + 1] === '\n') index += 1;
        result += 'n';
      } else if (char === '\n') {
        result += 'n';
      } else {
        result += char;
      }
      escaped = false;
      continue;
    }

    if (char === '\\') {
      result += char;
      escaped = true;
      continue;
    }

    if (char === '"') {
      result += char;
      inString = false;
      continue;
    }

    if (char === '\r') {
      if (text[index + 1] === '\n') index += 1;
      result += '\\n';
      continue;
    }

    if (char === '\n') {
      result += '\\n';
      continue;
    }

    result += char;
  }

  return result;
}

function parseGuideJson(text) {
  try {
    return JSON.parse(text);
  } catch (error) {
    return JSON.parse(escapeRawLineBreaksInJsonStrings(text));
  }
}

function normalizeMetaList(list) {
  return Array.isArray(list)
    ? list
        .map((item) => ({
          label: normalizeText(item && item.label),
          value: normalizeText(item && item.value),
          note: normalizeText(item && item.note),
        }))
        .filter(
          (item) =>
            hasText(item.label) || hasText(item.value) || hasText(item.note)
        )
    : [];
}

function normalizeTextBlockList(list) {
  return Array.isArray(list)
    ? list
        .map((item) => ({
          title: normalizeText(item && item.title),
          body_md: normalizeText(item && (item.body_md || item.body)),
        }))
        .filter((item) => hasText(item.title) || hasText(item.body_md))
    : [];
}

function makePortableGuide(source) {
  const base = cloneGuide({ ...emptyGuide, ...(source || {}) });
  const content = { ...emptyContent, ...(base.content || {}) };
  return {
    guide_key: normalizeText(base.guide_key),
    title: normalizeText(base.title),
    survey_code: normalizeText(base.survey_code),
    type_360: normalizeText(base.type_360, 'all') || 'all',
    relation_scope: ['all', 'self', 'other'].includes(base.relation_scope)
      ? base.relation_scope
      : 'all',
    sub_type: normalizeText(base.sub_type, 'all') || 'all',
    variant_key: normalizeText(base.variant_key, 'default') || 'default',
    priority: Number.isFinite(Number(base.priority)) ? Number(base.priority) : 10,
    content: {
      eyebrow: normalizeText(content.eyebrow),
      title: normalizeText(content.title),
      meta: normalizeMetaList(content.meta),
      sections: normalizeTextBlockList(content.sections),
      points: normalizeTextBlockList(content.points),
      note: normalizeText(content.note),
    },
  };
}

function makeGuideFromImport(source) {
  const portable = makePortableGuide(source);
  return {
    ...emptyGuide,
    ...portable,
    active: true,
    content: {
      ...emptyContent,
      ...(portable.content || {}),
    },
  };
}

function downloadJsonFile(fileName, data) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const SurveyGuideEditPage = () => {
  const { guideId } = useParams();
  const history = useHistory();
  const isNew = !guideId || guideId === 'new';
  const [guide, setGuide] = useState(cloneGuide(emptyGuide));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const allowNavigateRef = useRef(false);

  const executorToken =
    (typeof window !== 'undefined' && localStorage.getItem('w_auth')) || '';

  useEffect(() => {
    allowNavigateRef.current = false;
  }, [guideId]);

  useEffect(() => {
    if (isNew) return;
    const fetchGuide = async () => {
      setLoading(true);
      try {
        const res = await Axios.get(`/api/surveyGuide/${guideId}`, {
          headers: { 'x-user-token': executorToken },
        });
        if (res.data && res.data.success) {
          setGuide(cloneGuide(res.data.guide));
        } else {
          message.error('回答案内の取得に失敗しました');
        }
      } catch (error) {
        console.error(error);
        message.error('回答案内の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchGuide();
  }, [executorToken, guideId, isNew]);

  useEffect(() => {
    if (!dirty) return undefined;
    const unblock = history.block(() => {
      if (allowNavigateRef.current) return undefined;
      return '保存していない変更があります。移動しますか？';
    });
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [dirty, history]);

  const previewContent = useMemo(
    () => makePortableGuide(guide).content || emptyContent,
    [guide]
  );

  const exportJson = () => {
    const portable = makePortableGuide(guide);
    const fileName = `${safeFileName(portable.guide_key || portable.title)}.json`;
    downloadJsonFile(fileName, portable);
  };

  const exportTemplateJson = () => {
    downloadJsonFile('survey-guide-template.json', makePortableGuide(emptyGuide));
  };

  const openImportModal = () => {
    setJsonText('');
    setJsonModalOpen(true);
  };

  const readJsonFile = (file) => {
    const isJsonFile =
      file.type === 'application/json' ||
      String(file.name || '').toLowerCase().endsWith('.json');
    if (!isJsonFile) {
      message.error('JSONファイルを選択してください');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 1024 * 1024) {
      message.error('1MB以下のJSONファイルを選択してください');
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setJsonText(String(reader.result || ''));
      message.success('JSONファイルを読み込みました。取込ボタンで反映してください。');
    };
    reader.onerror = () => {
      message.error('JSONファイルの読み込みに失敗しました');
    };
    reader.readAsText(file);
    return false;
  };

  const importJson = () => {
    try {
      const parsed = parseGuideJson(jsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        message.error('JSONオブジェクトを入力してください');
        return;
      }
      setGuide(makeGuideFromImport(parsed));
      setDirty(true);
      setJsonModalOpen(false);
      message.success('JSONを取り込みました。保存するまでDBには反映されません。');
    } catch (error) {
      console.error(error);
      message.error('JSONの形式が正しくありません');
    }
  };

  const patchGuide = (patch) => {
    setGuide((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const patchContent = (patch) => {
    setGuide((prev) => ({
      ...prev,
      content: { ...(prev.content || emptyContent), ...patch },
    }));
    setDirty(true);
  };

  const patchArrayItem = (field, index, patch) => {
    const list = [...((guide.content && guide.content[field]) || [])];
    list[index] = { ...list[index], ...patch };
    patchContent({ [field]: list });
  };

  const addArrayItem = (field, item) => {
    const list = [...((guide.content && guide.content[field]) || []), item];
    patchContent({ [field]: list });
  };

  const removeArrayItem = (field, index) => {
    const list = [...((guide.content && guide.content[field]) || [])];
    list.splice(index, 1);
    patchContent({ [field]: list });
  };

  const validate = () => {
    if (!guide.guide_key.trim()) return 'guide_key は必須です';
    if (!guide.title?.trim()) return 'タイトルは必須です';
    if (!guide.survey_code.trim()) return 'サーベイコードは必須です';
    if (!guide.content?.title?.trim()) return 'ガイド本文タイトルは必須です';
    return '';
  };

  const save = async () => {
    const error = validate();
    if (error) {
      message.error(error);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...guide,
        ...makePortableGuide(guide),
        active: guide.active,
      };
      const res = isNew
        ? await Axios.post('/api/surveyGuide', payload, {
            headers: { 'x-user-token': executorToken },
          })
        : await Axios.patch(`/api/surveyGuide/${guideId}`, payload, {
            headers: { 'x-user-token': executorToken },
          });
      if (res.data && res.data.success) {
        message.success('保存しました');
        setDirty(false);
        if (isNew) {
          allowNavigateRef.current = true;
          history.replace(`/surveyguide/${res.data.guide._id}/edit`);
        } else {
          setGuide(cloneGuide(res.data.guide));
        }
      } else {
        message.error(res.data?.error || '保存に失敗しました');
      }
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.error || '保存に失敗しました'
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: '回答案内を削除しますか？',
      content:
        '削除すると元に戻せません。誤って作成した回答案内など、不要なデータのみ削除してください。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        setDeleting(true);
        try {
          const res = await Axios.delete(`/api/surveyGuide/${guideId}`, {
            headers: { 'x-user-token': executorToken },
          });
          if (res.data && res.data.success) {
            message.success('削除しました');
            setDirty(false);
            allowNavigateRef.current = true;
            history.push('/surveyguidemgr');
          } else {
            message.error(res.data?.error || '削除に失敗しました');
          }
        } catch (err) {
          console.error(err);
          message.error(err.response?.data?.error || '削除に失敗しました');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <Spin tip="読み込み中..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title={isNew ? '回答案内 新規作成' : '回答案内 編集'}
        subtitle="左側で編集し、右側で実際の表示を確認できます。保存すると受講者画面へ反映されます。"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={exportTemplateJson}>
              初期テンプレート出力
            </Button>
            <Button icon={<DownloadOutlined />} onClick={exportJson}>
              現在のJSON出力
            </Button>
            <Button icon={<ImportOutlined />} onClick={openImportModal}>
              JSON取込
            </Button>
            {!isNew && (
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
                onClick={confirmDelete}
              >
                削除
              </Button>
            )}
            <Button onClick={() => history.push('/surveyguidemgr')}>
              一覧へ戻る
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={save}
            >
              保存
            </Button>
          </Space>
        }
      />
      {dirty && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="保存していない変更があります。"
        />
      )}
      <Modal
        title={jsonImportTitle}
        open={jsonModalOpen}
        okText="取込"
        cancelText="キャンセル"
        onOk={importJson}
        onCancel={() => setJsonModalOpen(false)}
        width={720}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="JSONを取り込むとフォーム内容が置き換わります。文字列内の改行も取り込まれます。保存するまでDBには反映されません。"
        />
        <Upload
          accept=".json,application/json"
          beforeUpload={readJsonFile}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />} style={{ marginBottom: 12 }}>
            JSONファイル選択
          </Button>
        </Upload>
        <TextArea
          rows={18}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder="現在のJSON出力または初期テンプレート出力した内容を貼り付けてください。"
        />
      </Modal>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <div>
          <SectionCard title="基本情報" style={{ marginBottom: 16 }}>
            <Form layout="vertical" requiredMark={false}>
              <Form.Item
                label={requiredLabel(
                  helpLabel('管理キー（guide_key）', basicInfoHelps.guideKey)
                )}
              >
                <Input
                  value={guide.guide_key}
                  onChange={(e) => patchGuide({ guide_key: e.target.value })}
                  placeholder="例: 60020_bsfs_self"
                />
              </Form.Item>
              <Form.Item
                label={requiredLabel(
                  helpLabel('管理用タイトル', basicInfoHelps.title)
                )}
              >
                <Input
                  value={guide.title}
                  onChange={(e) => patchGuide({ title: e.target.value })}
                  placeholder="例: フォロワーシップ診断 アドバンス 本人用"
                />
              </Form.Item>
              <Space size="large" style={{ display: 'flex' }}>
                <Form.Item
                  label={requiredLabel(
                    helpLabel('サーベイコード', basicInfoHelps.surveyCode)
                  )}
                  style={{ flex: 1 }}
                >
                  <Input
                    value={guide.survey_code}
                    onChange={(e) => patchGuide({ survey_code: e.target.value })}
                    placeholder="例: 60020"
                  />
                </Form.Item>
                <Form.Item
                  label={helpLabel('優先度', basicInfoHelps.priority)}
                >
                  <InputNumber
                    value={guide.priority}
                    onChange={(value) => patchGuide({ priority: value || 0 })}
                    style={{ width: 120 }}
                  />
                </Form.Item>
              </Space>
              <Space size="large" style={{ display: 'flex' }}>
                <Form.Item
                  label={helpLabel('診断種別（type_360）', basicInfoHelps.type360)}
                  style={{ flex: 1 }}
                >
                  <Input
                    value={guide.type_360}
                    onChange={(e) =>
                      patchGuide({ type_360: e.target.value || 'all' })
                    }
                    placeholder="例: bsfs / bsfs_basic / all"
                  />
                </Form.Item>
                <Form.Item
                  label={helpLabel('回答者区分', basicInfoHelps.relationScope)}
                  style={{ flex: 1 }}
                >
                  <Select
                    value={guide.relation_scope}
                    onChange={(value) => patchGuide({ relation_scope: value })}
                  >
                    <Select.Option value="all">all（共通）</Select.Option>
                    <Select.Option value="self">self（本人用）</Select.Option>
                    <Select.Option value="other">other（協力者用）</Select.Option>
                  </Select>
                </Form.Item>
              </Space>
              <Space size="large" style={{ display: 'flex' }}>
                <Form.Item
                  label={helpLabel('サブタイプ（sub_type）', basicInfoHelps.subType)}
                  style={{ flex: 1 }}
                >
                  <Input
                    value={guide.sub_type}
                    onChange={(e) =>
                      patchGuide({ sub_type: e.target.value || 'all' })
                    }
                    placeholder="例: all"
                  />
                </Form.Item>
                <Form.Item
                  label={helpLabel(
                    'バリエーション（variant_key）',
                    basicInfoHelps.variantKey
                  )}
                  style={{ flex: 1 }}
                >
                  <Input
                    value={guide.variant_key}
                    onChange={(e) =>
                      patchGuide({ variant_key: e.target.value || 'default' })
                    }
                    placeholder="例: default / pre / post"
                  />
                </Form.Item>
              </Space>
            </Form>
          </SectionCard>

          <SectionCard
            title={variableHelpLabel('表示内容')}
            style={{ marginBottom: 16 }}
          >
            <Form layout="vertical" requiredMark={false}>
              <Form.Item label="アイブロウ">
                <Input
                  value={guide.content?.eyebrow}
                  onChange={(e) => patchContent({ eyebrow: e.target.value })}
                />
              </Form.Item>
              <Form.Item
                label={requiredLabel(variableHelpLabel('ガイド本文タイトル'))}
              >
                <Input
                  value={guide.content?.title}
                  onChange={(e) => patchContent({ title: e.target.value })}
                />
              </Form.Item>
              <Form.Item label={noteHelpLabel}>
                <TextArea
                  rows={3}
                  value={guide.content?.note}
                  onChange={(e) => patchContent({ note: e.target.value })}
                />
              </Form.Item>
            </Form>
          </SectionCard>

          <SectionCard
            title="メタ情報"
            extra={
              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  addArrayItem('meta', { label: '', value: '', note: '' })
                }
              >
                追加
              </Button>
            }
            style={{ marginBottom: 16 }}
          >
            {(guide.content?.meta || []).map((item, index) => (
              <Space
                key={`meta-${index}`}
                align="start"
                style={{ display: 'flex', marginBottom: 8 }}
              >
                <Input
                  value={item.label}
                  onChange={(e) =>
                    patchArrayItem('meta', index, { label: e.target.value })
                  }
                  placeholder="ラベル"
                />
                <Input
                  value={item.value}
                  onChange={(e) =>
                    patchArrayItem('meta', index, { value: e.target.value })
                  }
                  placeholder="値"
                />
                <Input
                  value={item.note}
                  onChange={(e) =>
                    patchArrayItem('meta', index, { note: e.target.value })
                  }
                  placeholder="補足"
                />
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeArrayItem('meta', index)}
                />
              </Space>
            ))}
          </SectionCard>

          <SectionCard
            title="セクション"
            extra={
              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  addArrayItem('sections', { title: '', body_md: '' })
                }
              >
                追加
              </Button>
            }
            style={{ marginBottom: 16 }}
          >
            {(guide.content?.sections || []).map((item, index) => (
              <div key={`section-${index}`} style={{ marginBottom: 18 }}>
                <Space style={{ display: 'flex', marginBottom: 8 }}>
                  <Input
                    value={item.title}
                    onChange={(e) =>
                      patchArrayItem('sections', index, {
                        title: e.target.value,
                      })
                    }
                    placeholder="セクションタイトル"
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeArrayItem('sections', index)}
                  />
                </Space>
                <div style={{ marginBottom: 6 }}>
                  {markdownHelpLabel('本文（Markdown）')}
                </div>
                <TextArea
                  rows={7}
                  value={item.body_md}
                  onChange={(e) =>
                    patchArrayItem('sections', index, {
                      body_md: e.target.value,
                    })
                  }
                  placeholder="Markdown本文"
                />
              </div>
            ))}
          </SectionCard>

          <SectionCard
            title="番号付きポイント"
            extra={
              <Button
                icon={<PlusOutlined />}
                onClick={() => addArrayItem('points', { title: '', body_md: '' })}
              >
                追加
              </Button>
            }
          >
            {(guide.content?.points || []).map((item, index) => (
              <div key={`point-${index}`} style={{ marginBottom: 18 }}>
                <Space style={{ display: 'flex', marginBottom: 8 }}>
                  <Input
                    value={item.title}
                    onChange={(e) =>
                      patchArrayItem('points', index, {
                        title: e.target.value,
                      })
                    }
                    placeholder="ポイントタイトル"
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeArrayItem('points', index)}
                  />
                </Space>
                <div style={{ marginBottom: 6 }}>
                  {markdownHelpLabel('本文（Markdown）')}
                </div>
                <TextArea
                  rows={4}
                  value={item.body_md}
                  onChange={(e) =>
                    patchArrayItem('points', index, {
                      body_md: e.target.value,
                    })
                  }
                  placeholder="Markdown本文"
                />
              </div>
            ))}
          </SectionCard>
        </div>

        <div style={{ position: 'sticky', top: 96 }}>
          <GuideContent content={previewContent} />
        </div>
      </div>
    </PageContainer>
  );
};

export default SurveyGuideEditPage;
